import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Mic, Plus, Printer, Search, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PostItCard } from "@/components/PostItCard";
import { PostItEditor } from "@/components/PostItEditor";
import { PostItVoiceDialog } from "@/components/PostItVoiceDialog";
import { API, api, errText } from "@/lib/api";
import { addDays, isoDate, shortDate } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

const FILTERS = [
  ["all", "Everything"],
  ["high", "High priority"],
  ["pinned", "Pinned"],
  ["linked", "Linked"],
  ["expired", "Expired"],
];

export default function PostIts() {
  const { canWrite, workspace } = useAuth();
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [links, setLinks] = useState({ matters: [], events: [], todos: [] });
  const [inbound, setInbound] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const orderRef = useRef([]);

  const load = useCallback(async () => {
    try {
      const today = new Date();
      const [n, m, ev, td, ib] = await Promise.all([
        api.get("/postits"),
        api.get("/matters"),
        api.get("/events", {
          params: { start: isoDate(addDays(today, -30)), end: isoDate(addDays(today, 120)) },
        }),
        api.get("/todos", {
          params: { start: isoDate(addDays(today, -30)), end: isoDate(addDays(today, 120)) },
        }),
        api.get("/inbound/status").catch(() => ({ data: null })),
      ]);
      setNotes(n.data);
      setLinks({
        matters: m.data.map((x) => ({
          value: x.matter_id,
          label: x.title,
          sub: [x.case_number, x.client_name].filter(Boolean).join(" · "),
        })),
        events: ev.data.map((x) => ({
          value: x.event_id,
          label: x.title,
          sub: `${shortDate(x.date)}${x.court_name ? ` · ${x.court_name}` : ""}`,
        })),
        todos: td.data.map((x) => ({
          value: x.todo_id,
          label: x.title,
          sub: shortDate(x.date),
        })),
      });
      setInbound(ib.data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [workspace?.workspace_id]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return notes.filter((n) => {
      if (filter === "high" && n.priority !== "high") return false;
      if (filter === "pinned" && !n.pinned) return false;
      if (filter === "expired" && !n.expired) return false;
      if (filter === "linked" && !(n.matter_id || n.event_id || n.todo_id)) return false;
      if (!needle) return true;
      return [n.title, n.body, n.matter_title, n.event_label, n.todo_title]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle));
    });
  }, [notes, filter, query]);

  const canArrange = canWrite && filter === "all" && !query.trim();

  const persistOrder = async (ordered) => {
    setNotes(ordered);
    try {
      await api.post("/postits/reorder", { ids: ordered.map((n) => n.post_it_id) });
      load();
    } catch (e) {
      toast.error(errText(e));
      load();
    }
  };

  const moveBy = (note, delta) => {
    const list = [...notes];
    const from = list.findIndex((n) => n.post_it_id === note.post_it_id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= list.length) return;
    list.splice(to, 0, list.splice(from, 1)[0]);
    persistOrder(list);
  };

  const dropOn = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const list = [...notes];
    const from = list.findIndex((n) => n.post_it_id === dragId);
    const to = list.findIndex((n) => n.post_it_id === targetId);
    if (from < 0 || to < 0) return;
    list.splice(to, 0, list.splice(from, 1)[0]);
    setDragId(null);
    persistOrder(list);
  };

  const togglePin = async (note) => {
    try {
      await api.put(`/postits/${note.post_it_id}`, { pinned: !note.pinned });
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (note) => {
    try {
      await api.delete(`/postits/${note.post_it_id}`);
      toast.success("Note binned");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const spawn = async (note, kind, offset) => {
    const day = isoDate(addDays(new Date(), offset));
    try {
      const { data } = await api.post(`/postits/${note.post_it_id}/${kind}`, { date: day });
      load();
      toast.success(
        kind === "todo" ? `Added to the ${offset ? "tomorrow" : "today"} to-do list` : "Put on the diary",
        {
          description: `${data[kind].title} · ${shortDate(day)}`,
          duration: 8000,
          action: {
            label: kind === "todo" ? "Open to-dos" : "Open diary",
            onClick: () =>
              navigate(kind === "todo" ? `/todos?date=${day}` : `/calendar?date=${day}&view=day`),
          },
        },
      );
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const assetUrl = (note, kind) =>
    `${API}/postits/${note.post_it_id}/${kind}?v=${encodeURIComponent(note.updated_at)}` +
    (workspace?.workspace_id ? `&workspace_id=${workspace.workspace_id}` : "");

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  orderRef.current = notes;

  return (
    <div className="space-y-8" data-testid="postits-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="min-w-0">
          <p className="label-caps inline-flex items-center gap-2">
            <StickyNote className="h-4 w-4" /> Post-Its
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">
            {notes.length} note{notes.length === 1 ? "" : "s"} on the board
          </h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
            The monitor edge, digitised. Type a thought, dictate it, or email it in — then colour it,
            texture it, give it a shelf life and hang it off a matter, a hearing or a to-do.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-testid="postit-snapshot-btn"
            onClick={() =>
              window.open(
                `/postits/print?filter=${filter}&q=${encodeURIComponent(query.trim())}`,
                "_blank",
                "noopener",
              )
            }
            className="rounded-full h-12 px-5"
          >
            <Printer className="h-5 w-5 mr-2" /> Board snapshot
          </Button>
          {canWrite ? (
            <>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="postit-email-btn"
                  className="rounded-full h-12 px-5"
                >
                  <Mail className="h-5 w-5 mr-2" /> By email
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80">
                <p className="label-caps">Email a note in</p>
                {inbound?.enabled && inbound?.postit_address ? (
                  <>
                    <p className="mt-2 text-sm">
                      Send from <strong>{inbound.send_from}</strong> to:
                    </p>
                    <p
                      className="mt-1 text-sm font-mono break-all"
                      data-testid="postit-inbound-address"
                    >
                      {inbound.postit_address}
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      The subject becomes the heading, the body becomes the note. Attach a voice
                      memo and it is transcribed instead.
                    </p>
                  </>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground" data-testid="postit-inbound-off">
                    Email intake is ready but switched off — set it up under Settings → Email in and
                    notes will start arriving here.
                  </p>
                )}
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              data-testid="postit-dictate-btn"
              onClick={() => setVoiceOpen(true)}
              className="rounded-full h-12 px-5"
            >
              <Mic className="h-5 w-5 mr-2" /> Dictate
            </Button>
            <Button
              data-testid="postit-new-btn"
              onClick={openNew}
              className="rounded-full h-12 px-6 text-base"
            >
              <Plus className="h-5 w-5 mr-2" /> New note
            </Button>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([value, label]) => (
          <button
            key={value}
            data-testid={`postit-filter-${value}`}
            onClick={() => setFilter(value)}
            className={`px-4 py-2.5 rounded-full border text-sm font-semibold transition-colors duration-200 ${
              filter === value
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border hover:bg-accent"
            }`}
          >
            {label}
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="postit-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the board"
            className="rounded-full h-11 pl-9 text-base"
          />
        </div>
      </div>

      {shown.length ? (
        <div
          className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5"
          data-testid="postit-board"
        >
          {shown.map((note) => (
            <PostItCard
              key={note.post_it_id}
              note={note}
              canWrite={canWrite}
              imageUrl={note.has_image ? assetUrl(note, "image") : null}
              audioUrl={note.has_audio ? assetUrl(note, "audio") : null}
              dragging={dragId === note.post_it_id}
              onEdit={(n) => {
                setEditing(n);
                setEditorOpen(true);
              }}
              onDelete={remove}
              onPin={togglePin}
              onMove={moveBy}
              onSpawn={spawn}
              dragHandlers={
                canArrange
                  ? {
                      draggable: true,
                      onDragStart: () => setDragId(note.post_it_id),
                      onDragEnd: () => setDragId(null),
                      onDragOver: (e) => e.preventDefault(),
                      onDrop: (e) => {
                        e.preventDefault();
                        dropOn(note.post_it_id);
                      },
                    }
                  : {}
              }
            />
          ))}
        </div>
      ) : (
        <p
          className="text-sm text-muted-foreground border border-dashed border-border rounded-sm px-4 py-10 text-center"
          data-testid="postit-empty"
        >
          {notes.length
            ? "Nothing matches that filter."
            : "Nothing on the board yet. Stick up your first thought."}
        </p>
      )}

      <PostItEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        note={editing}
        links={links}
        imageUrl={editing?.has_image ? assetUrl(editing, "image") : null}
        onSaved={load}
      />

      <PostItVoiceDialog
        open={voiceOpen}
        onOpenChange={setVoiceOpen}
        onCreated={(note) => {
          load();
          if (note?.pending_voice) return;
          setEditing(note);
          setEditorOpen(true);
        }}
      />
    </div>
  );
}
